import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import './App.css';
import Login from './components/Login';
import AppsLanding from './components/AppsLanding';
import UserConfiguration from './components/UserConfiguration';
import ResultsView from './components/ResultsView';
import Analytics from './components/Analytics';
import PortHealth from './components/PortHealth';
import AccountTransfer from './components/AccountTransfer';
import LegalBankruptcy from './components/LegalBankruptcy';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import AuthenticationWrapper from './components/AuthenticationWrapper';
import ConnectionStatus from './components/ConnectionStatus';

const msalInstance = new PublicClientApplication(msalConfig);

const AppRoutes: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();

  return (
    <Routes>
      {/* Public route - Login */}
      <Route path="/" element={
        isAuthenticated ? <Navigate to="/apps" replace /> : <Login />
      } />

      {/* Protected route - Apps Landing */}
      <Route path="/apps" element={
        <ProtectedRoute>
          <AppsLanding />
        </ProtectedRoute>
      } />

      {/* Protected routes - Port Reallocation Tool */}
      <Route path="/port-reallocation/*" element={
        <ProtectedRoute>
          <div className="App">
            <Header 
              appName="Port Reallocation Tool"
              navLinks={[
                { to: '/port-reallocation/config', label: 'Configuration' },
                { to: '/port-reallocation/analytics', label: 'Analytics' },
                { to: '/port-reallocation/results', label: 'Results' }
              ]}
            />
            <ConnectionStatus apiUrl={process.env.REACT_APP_API_URL || 'http://localhost:5000/api'} />
            <main className="main-content">
              <Routes>
                <Route path="config" element={<UserConfiguration />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="results" element={<ResultsView />} />
                <Route path="*" element={<Navigate to="/port-reallocation/config" replace />} />
              </Routes>
            </main>
          </div>
        </ProtectedRoute>
      } />

      {/* Protected routes - Port Health Monitoring */}
      <Route path="/port-health" element={
        <ProtectedRoute>
          <div className="App">
            <Header 
              appName="Port Health Monitoring"
              navLinks={[]}
            />
            <ConnectionStatus apiUrl={process.env.REACT_APP_API_URL || 'http://localhost:5000/api'} />
            <main className="main-content">
              <PortHealth />
            </main>
          </div>
        </ProtectedRoute>
      } />

      {/* Protected routes - Legal & Bankruptcy */}
      <Route path="/legal-bankruptcy" element={
        <ProtectedRoute>
          <div className="App">
            <Header 
              appName="Legal & Bankruptcy"
              navLinks={[]}
            />
            <ConnectionStatus apiUrl={process.env.REACT_APP_API_URL || 'http://localhost:5000/api'} />
            <main className="main-content">
              <LegalBankruptcy />
            </main>
          </div>
        </ProtectedRoute>
      } />

      {/* Protected routes - Account Transfer */}
      <Route path="/account-transfer" element={
        <ProtectedRoute>
          <div className="App">
            <Header 
              appName="Account Transfer"
              navLinks={[]}
            />
            <ConnectionStatus apiUrl={process.env.REACT_APP_API_URL || 'http://localhost:5000/api'} />
            <main className="main-content">
              <AccountTransfer />
            </main>
          </div>
        </ProtectedRoute>
      } />

      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const [msalReady, setMsalReady] = useState(false);

  useEffect(() => {
    // Initialize MSAL before rendering the app
    msalInstance.initialize()
      .then(() => {
        console.log('MSAL initialized successfully');
        setMsalReady(true);
      })
      .catch((error) => {
        console.error('MSAL initialization failed:', error);
        // Still set ready to show error to user
        setMsalReady(true);
      });
  }, []);

  if (!msalReady) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        fontSize: '1.5rem'
      }}>
        <div>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>⏳</div>
          <div>Initializing...</div>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticationWrapper>
        <Router>
          <AppRoutes />
        </Router>
      </AuthenticationWrapper>
    </MsalProvider>
  );
}

export default App;
